import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import {
  ReferenceUserDataService,
  ReferenceUserData,
  UpdateReferenceUserDataPayload
} from '../../services/reference-user-data.service';

/**
 * แปลงข้อความเป็นรูปแบบเลขบัตรประชาชนไทย: X XXXX XXXXX XX X (กลุ่ม 1-4-5-2-1)
 * - รองรับเลขไทย ๐-๙ และตัดอักขระอื่นทิ้ง
 * - จำกัดไม่เกิน 13 หลัก และใส่ช่องว่างให้อัตโนมัติ
 */
export function formatThaiCid(value: string | null | undefined): string {
  const thaiDigits = '๐๑๒๓๔๕๖๗๘๙';
  const arabic = String(value ?? '').replace(/[๐-๙]/g, (ch) => String(thaiDigits.indexOf(ch)));
  const digits = arabic.replace(/\D/g, '').slice(0, 13);

  const parts: string[] = [];
  if (digits.length > 0) parts.push(digits.slice(0, 1));
  if (digits.length > 1) parts.push(digits.slice(1, 5));
  if (digits.length > 5) parts.push(digits.slice(5, 10));
  if (digits.length > 10) parts.push(digits.slice(10, 12));
  if (digits.length > 12) parts.push(digits.slice(12, 13));
  return parts.join(' ');
}

/**
 * Validator: ต้องเป็นเลขบัตรประชาชนครบ 13 หลัก (ไม่นับช่องว่าง)
 */
export function thaiCidValidator(control: AbstractControl): ValidationErrors | null {
  const digits = String(control.value ?? '').replace(/\D/g, '');
  if (!digits) {
    return null; // ปล่อยให้ Validators.required จัดการกรณีค่าว่าง
  }
  return digits.length === 13 ? null : { thaiCid: true };
}

@Component({
  selector: 'app-edit-reference-user-data-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule
  ],
  templateUrl: './edit-reference-user-data-modal.component.html',
  styleUrl: './edit-reference-user-data-modal.component.scss'
})
export class EditReferenceUserDataModalComponent implements OnInit, OnDestroy {
  @Input() recordData?: ReferenceUserData; // ข้อมูลที่ส่งมาจาก parent (เฉพาะโหมดแก้ไข)
  @Input() readOnly = false; // โหมดดูอย่างเดียว (สำหรับผู้ใช้ที่สมัครแล้ว)
  @Input() mode: 'create' | 'edit' = 'edit'; // โหมดของฟอร์ม

  editForm!: FormGroup;
  isSubmitting = false;
  errorMessage: string | null = null;

  // รายชื่อเดือนภาษาอังกฤษ (ใช้ในตัวเลือกวันหมดอายุ)
  readonly months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // รายการปี พ.ศ. สำหรับ dropdown (เรียงจากใหม่ไปเก่า)
  years: string[] = [];

  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private referenceUserDataService: ReferenceUserDataService
  ) {
    this.buildYearOptions();
    this.initForm();
  }

  /** สร้างรายการปี พ.ศ. ตั้งแต่ปีปัจจุบัน+10 ย้อนลงไปถึง 2500 */
  private buildYearOptions(): void {
    const currentBE = new Date().getFullYear() + 543;
    const maxYear = currentBE + 10;
    const minYear = 2500;
    const list: string[] = [];
    for (let y = maxYear; y >= minYear; y--) {
      list.push(String(y));
    }
    this.years = list;
  }

  ngOnInit(): void {
    // โหมดแก้ไข: เติมข้อมูลเดิมลงฟอร์ม / โหมดสร้าง: เริ่มจากฟอร์มว่าง
    if (this.mode === 'edit' && this.recordData) {
      this.populateForm();
    }

    // เฝ้าดูตัวเลือกวันหมดอายุ (ตลอดชีพ / วัน-เดือน-ปี) เพื่อประกอบเป็น string ที่จะบันทึก
    this.setupExpirationWatchers();

    // โหมดดูอย่างเดียว: ปิดการแก้ไขทุกฟิลด์ (เฉพาะโหมดแก้ไข)
    if (this.readOnly) {
      this.editForm.disable();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private initForm(): void {
    // ตอนแก้ไขไม่บังคับกรอกทุกช่อง — ทุกฟิลด์เป็น optional
    // เลขบัตรประชาชนจะจัดรูปแบบอัตโนมัติ และตรวจครบ 13 หลักเฉพาะเมื่อมีการกรอกเท่านั้น
    this.editForm = this.fb.group({
      idCardNumber: ['', [thaiCidValidator]],
      title: ['', [Validators.maxLength(50)]],
      name: ['', [Validators.maxLength(255)]],
      lastname: ['', [Validators.maxLength(255)]],
      idMember: ['', [Validators.maxLength(50)]],
      // ค่าที่จะบันทึกจริง (ประกอบจากตัวเลือกด้านล่างแบบอัตโนมัติ)
      expirationDate: ['', [Validators.maxLength(255)]],
      // ตัวช่วยกรอกวันหมดอายุ (ไม่ส่งตรงไป backend)
      expLifetime: [false],
      expDay: [''],
      expMonth: [''],
      expYear: ['']
    });
  }

  private populateForm(): void {
    if (!this.recordData) {
      return;
    }
    const parsed = this.parseExpiration(this.recordData.expirationDate);

    // เผื่อกรณีปีเดิมอยู่นอกช่วง dropdown -> เพิ่มเข้าไปแล้วเรียงใหม่
    if (parsed.year && !this.years.includes(parsed.year)) {
      this.years = [...this.years, parsed.year].sort((a, b) => Number(b) - Number(a));
    }

    this.editForm.patchValue({
      // จัดรูปแบบเลขบัตรให้เป็น pattern มาตรฐานตั้งแต่ตอนโหลด
      idCardNumber: formatThaiCid(this.recordData.idCardNumber || ''),
      title: this.recordData.title || '',
      name: this.recordData.name || '',
      lastname: this.recordData.lastname || '',
      idMember: this.recordData.idMember || '',
      expirationDate: this.recordData.expirationDate || '',
      expLifetime: parsed.lifetime,
      expDay: parsed.day,
      expMonth: parsed.month,
      expYear: parsed.year
    });
  }

  /**
   * แยกส่วนค่าวันหมดอายุเดิมออกเป็น ตลอดชีพ / วัน-เดือน-ปี
   * รองรับรูปแบบ "27 April 2564", "ตลอดชีพ" และค่าว่าง
   */
  private parseExpiration(value: string | null | undefined): {
    lifetime: boolean;
    day: string;
    month: string;
    year: string;
  } {
    const v = (value || '').trim();
    if (!v) {
      return { lifetime: false, day: '', month: '', year: '' };
    }
    if (v.includes('ตลอดชีพ')) {
      return { lifetime: true, day: '', month: '', year: '' };
    }
    // รูปแบบ: วันที่ ชื่อเดือนอังกฤษ ปีพ.ศ.
    const match = v.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{3,4})$/);
    if (match) {
      const monthName =
        this.months.find((m) => m.toLowerCase() === match[2].toLowerCase()) || '';
      return { lifetime: false, day: match[1], month: monthName, year: match[3] };
    }
    // รูปแบบอื่นๆ ที่แยกไม่ได้ -> ไม่เติมค่าในตัวช่วย (ผู้ใช้เลือกใหม่ได้)
    return { lifetime: false, day: '', month: '', year: '' };
  }

  /**
   * เฝ้าดูตัวช่วยกรอกวันหมดอายุ แล้วประกอบเป็น string สำหรับบันทึก
   */
  private setupExpirationWatchers(): void {
    const recompute = () => this.updateExpirationDate();

    ['expLifetime', 'expDay', 'expMonth', 'expYear'].forEach((name) => {
      const control = this.editForm.get(name);
      if (control) {
        this.subscription.add(control.valueChanges.subscribe(recompute));
      }
    });

    // ตั้งค่า enable/disable ของวัน-เดือน-ปี ตามสถานะตลอดชีพครั้งแรก
    this.applyLifetimeState(this.editForm.get('expLifetime')?.value);
  }

  /** เปิด/ปิดการกรอกวัน-เดือน-ปี ตามสถานะ "ตลอดชีพ" */
  private applyLifetimeState(isLifetime: boolean): void {
    if (this.readOnly) {
      return; // โหมดดูอย่างเดียวจะถูก disable ทั้งฟอร์มอยู่แล้ว
    }
    ['expDay', 'expMonth', 'expYear'].forEach((name) => {
      const control = this.editForm.get(name);
      if (!control) {
        return;
      }
      if (isLifetime) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  /** ประกอบค่าวันหมดอายุจากตัวช่วย แล้วเก็บลง control expirationDate */
  private updateExpirationDate(): void {
    const isLifetime = !!this.editForm.get('expLifetime')?.value;
    this.applyLifetimeState(isLifetime);

    let result = '';
    if (isLifetime) {
      result = 'ตลอดชีพ';
    } else {
      const day = String(this.editForm.get('expDay')?.value || '').replace(/\D/g, '');
      const month = String(this.editForm.get('expMonth')?.value || '').trim();
      const year = String(this.editForm.get('expYear')?.value || '').replace(/\D/g, '');
      if (day && month && year) {
        // ตัด 0 นำหน้าของวันออก เช่น "07" -> "7"
        result = `${parseInt(day, 10)} ${month} ${year}`;
      }
    }

    this.editForm.get('expirationDate')?.setValue(result, { emitEvent: false });
  }

  /** จำกัดให้พิมพ์เฉพาะตัวเลขในช่องวันที่/ปี */
  onExpDigitsInput(controlName: 'expDay' | 'expYear', maxLen: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, maxLen);
    this.editForm.get(controlName)?.setValue(digits);
    input.value = digits;
  }

  /**
   * จัดรูปแบบเลขบัตรประชาชนอัตโนมัติขณะพิมพ์ (ใส่ช่องว่างให้เอง)
   * ผู้ใช้พิมพ์แค่ตัวเลข ระบบจะจัดเป็น X XXXX XXXXX XX X ให้
   */
  onIdCardInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = formatThaiCid(input.value);
    const control = this.editForm.get('idCardNumber');
    control?.setValue(formatted);
    // อัปเดตค่าใน DOM ด้วย เพื่อให้ตำแหน่ง cursor อยู่ท้ายสุดอย่างสม่ำเสมอ
    input.value = formatted;
  }

  // helper สำหรับ template ตรวจสอบสถานะ field
  isInvalid(controlName: string): boolean {
    const control = this.editForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  onSubmit(): void {
    // โหมดดูอย่างเดียว: ไม่อนุญาตให้บันทึก
    if (this.readOnly) {
      return;
    }

    this.errorMessage = null;

    if (this.editForm.invalid) {
      Object.keys(this.editForm.controls).forEach(key => {
        this.editForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSubmitting = true;

    // ประกอบ payload จากฟอร์ม
    const raw = this.editForm.getRawValue();
    const expiration = (raw.expirationDate || '').trim();
    const payload: UpdateReferenceUserDataPayload = {
      idCardNumber: (raw.idCardNumber || '').trim(),
      title: (raw.title || '').trim(),
      name: (raw.name || '').trim(),
      lastname: (raw.lastname || '').trim(),
      idMember: (raw.idMember || '').trim(),
      expirationDate: expiration === '' ? null : expiration
    };

    const request$ = this.mode === 'create'
      ? this.referenceUserDataService.createReferenceUserData(payload)
      : this.referenceUserDataService.updateReferenceUserData(this.recordData!.id, payload);

    const sub = request$.subscribe({
      next: (result) => {
        this.isSubmitting = false;
        this.activeModal.close({
          success: true,
          action: this.mode,
          record: result
        });
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage =
          error?.error?.message ||
          (this.mode === 'create'
            ? 'ไม่สามารถเพิ่มข้อมูลได้ กรุณาลองใหม่อีกครั้ง'
            : 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
      }
    });

    this.subscription.add(sub);
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
